import { Link } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import type { ClientBanner } from '../../api/types.ts';
import {
  useClientContacts, useClientPeople, useClientSummary, type CaseContact, type LivesAt, type NextVisit, type SectionKey,
} from '../../api/navigation.ts';
import { usePastAddresses } from '../../api/pastAddresses.ts';
import { useClientIdentifiers, useRevealAllIdentifiers } from '../../api/clientDetails.ts';
import { ApiError } from '../../api/client.ts';
import { masked } from '../../identifiers/format.ts';
import { LesserFlags, ResolvedSafetyFlags } from '../../components/FlagBanner.tsx';
import { splitByBand } from '../../components/ClientHeader.tsx';
import { stateTag, StatusTag } from '../../components/StatusTag.tsx';
import { LoadFailure } from '../../components/LoadFailure.tsx';
import { ClientAvatar } from '../../components/ClientAvatar.tsx';
import { Panel } from '../../components/Panel.tsx';
import { ClientAccountsPanel } from '../../components/ClientAccountsPanel.tsx';
import { ContactLinkList } from '../../components/contacts/ContactLinkList.tsx';
import { AddContactSheet } from '../../components/contacts/AddContactSheet.tsx';
import { formatDate, parseDateOnly } from '../../dates.ts';
import { peopleEditPath } from './EditDetails.tsx';
import { LivesAtLines } from '../../components/LivesAtLines.tsx';

/**
 * The client dashboard, layout C (round 3, approved by Arthur and Judah,
 * 14 October; the mockup is procura-scratch/design/procura-layouts.html):
 *
 *   Client (4)            | Visits (4)          | Flags (4)
 *   People on the case    | What's due next (8)
 *     (4, two rows)       | Contacts (4)        | Past addresses (4)
 *   Client accounts (12), closed until opened (P-56)
 *
 * Every panel follows the sections: Visits and Past addresses need Social
 * Work; what is due needs Legal or Tasks; the Court and Counsel groups of
 * People on the case need Legal. The server sends each part only where the
 * section is held, so a panel that is not drawn was never sent either.
 */
export function ClientDashboardSection({
  clientId, banner, sections, mayRecordVisit, mayRecordPeople,
}: {
  clientId: string;
  banner: ClientBanner;
  sections: SectionKey[];
  mayRecordVisit: boolean;
  /** Legal at write (GET /me/sections): the people on the case have their own Edit button (A-#97). */
  mayRecordPeople: boolean;
}) {
  const summary = useClientSummary(clientId);
  const hasSw = sections.includes('sw');

  return (
    <div className="grid12">
      <ClientCard banner={banner} dateOfBirth={summary.data?.identity?.date_of_birth ?? null}
                  language={summary.data?.identity?.primary_language ?? null}
                  communicationNeeds={summary.data?.identity?.communication_needs ?? null}
                  aliases={summary.data?.aliases ?? []} mayEdit={summary.data?.may_edit_details === true}
                  livesAt={summary.data ? summary.data.lives_at ?? null : undefined}
                  identifiersVisible={summary.data?.identifiers_visible === true} />

      {hasSw && (
        <Panel
          title="Visits"
          span={4}
          actions={mayRecordVisit && !isClosed(banner.legal_status) ? (
            <Link to={`/clients/${clientId}/social-work/visit`}
                  className="inline-flex min-h-[44px] items-center rounded-md bg-brand px-4 text-sm font-semibold text-on-brand">
              Record a visit
            </Link>
          ) : undefined}
        >
          {summary.isPending ? <Waiting /> : summary.data?.next_visit ? (
            <VisitFacts v={summary.data.next_visit} />
          ) : (
            <p className="px-3 py-3 text-sm text-neutral-600">
              No visits are owed: {banner.visit_basis ?? 'no visiting standard applies to this client now'}.
            </p>
          )}
        </Panel>
      )}

      <FlagsPanel banner={banner} />

      <PeoplePanel clientId={clientId} hasLegal={sections.includes('legal')} mayEdit={sections.includes('legal') && mayRecordPeople} />

      {(sections.includes('legal') || sections.includes('tasks')) && (
        <Panel title="What’s due next" span={8}>
          {summary.error && !summary.data ? (
            <LoadFailure what="What is due" error={summary.error} onRetry={() => void summary.refetch()}
                         retrying={summary.isFetching} forbiddenIsSession={false} />
          ) : summary.isPending ? <Waiting /> : (
            <div className="px-3 py-2">
              {summary.data?.deadlines && (
                <DueList heading="Deadlines" empty="Nothing open."
                  items={summary.data.deadlines.map((d) => ({
                    key: d.id,
                    // 122: one line per property, naming it
                    label: d.property_name ? `${d.label} · ${d.property_name}` : d.label,
                    due: d.due_date, days: d.days_until,
                    note: d.is_provisional ? 'estimate' : d.is_statutory ? 'statutory' : null,
                    action: d.property_id && summary.data?.may_record_site_visit
                      ? { to: `/clients/${clientId}/property/${d.property_id}`, text: 'Record the visit', state: { record: 'site_visit' } }
                      : undefined,
                  }))} />
              )}
              {summary.data?.tasks && (
                <DueList heading="Tasks" empty="No open tasks."
                  items={summary.data.tasks.map((t) => ({
                    key: t.id, label: t.title, due: t.due_date,
                    days: t.days_overdue === null ? null : -t.days_overdue, note: t.assigned_to?.name ?? 'unassigned',
                  }))} />
              )}
              <p className="mt-1 flex flex-wrap gap-x-4 text-sm">
                {sections.includes('tasks') && <Link to={`/clients/${clientId}/tasks`} className="link inline-flex min-h-[44px] items-center">All tasks</Link>}
                {sections.includes('reports') && <Link to={`/clients/${clientId}/reports`} className="link inline-flex min-h-[44px] items-center">Reports</Link>}
              </p>
            </div>
          )}
        </Panel>
      )}

      <ContactsPanel clientId={clientId} />

      {hasSw && <PastAddressesPanel clientId={clientId} />}

      {/* P-56 (A-#506, A-#512): the client's online accounts and phones, closed until opened. Full width, so the
          six panels above keep layout C exactly as approved. */}
      <ClientAccountsPanel clientId={clientId} />
    </div>
  );
}

function isClosed(status: string | null) {
  return status === 'Deceased' || status === 'Deceased, discharged' || status === 'Discharged';
}

/** Whole years between a calendar day and today, both as calendar days (never through a timezone). */
function ageOn(dateOfBirth: string): number {
  const born = parseDateOnly(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - born.getFullYear();
  if (today.getMonth() < born.getMonth() || (today.getMonth() === born.getMonth() && today.getDate() < born.getDate())) age--;
  return age;
}

/**
 * The client: picture placeholder, name, reference in the bar, state, born, age, where they live, and
 * the names they go by (live aliases; never a corrected name's old value, which only the identity
 * history shows). Edit details, for a reader who may change something there.
 */
function ClientCard({ banner, dateOfBirth, language, communicationNeeds, aliases, mayEdit, identifiersVisible, livesAt }: {
  banner: ClientBanner; dateOfBirth: string | null; language: string | null; communicationNeeds: string | null;
  aliases: { id: string; alias_name: string }[]; mayEdit: boolean; identifiersVisible: boolean;
  /** undefined while the summary loads; null when no residence is recorded. */
  livesAt: LivesAt | null | undefined;
}) {
  const state = stateTag(banner.legal_status);
  return (
    <Panel title="Client" note={banner.client_ref} span={4}>
      <div className="grid gap-2.5 px-3 py-3">
        <ClientAvatar clientId={banner.client_id} displayName={banner.display_name} size={72} />
        <div>
          <h1 className="text-[1.25rem] leading-tight font-semibold text-neutral-900">{banner.display_name}</h1>
          <p className="mt-1">{state ? <StatusTag status={banner.legal_status} /> : <span className="text-sm text-neutral-700">Active</span>}</p>
        </div>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[0.9375rem]">
          <dt className="text-neutral-600">Born</dt>
          <dd className="tabular-nums text-neutral-900">{dateOfBirth ? formatDate(dateOfBirth) : 'Not recorded'}</dd>
          <dt className="text-neutral-600">Age</dt>
          <dd className="tabular-nums text-neutral-900">{dateOfBirth ? ageOn(dateOfBirth) : 'Not recorded'}</dd>
          {aliases.length > 0 && (
            <>
              <dt className="text-neutral-600">Also known as</dt>
              <dd className="text-neutral-900">{aliases.map((a) => a.alias_name).join('; ')}</dd>
            </>
          )}
          <dt className="text-neutral-600">Lives at</dt>
          <dd className="text-neutral-900">
            {banner.whereabouts_unknown ? (
              <span className="font-bold text-critical-edge">
                Whereabouts unknown
                {banner.where_to_find_them && <span className="block font-medium text-neutral-700">Last known: {banner.where_to_find_them}</span>}
              </span>
            ) : livesAt === undefined ? null : livesAt ? <LivesAtLines at={livesAt} /> : banner.where_to_find_them ?? 'Not recorded'}
          </dd>
          {/* Only when recorded (Arthur, 15 October): found in the field, changed from Edit details. */}
          {language && (
            <>
              <dt className="text-neutral-600">Language</dt>
              <dd className="text-neutral-900">{language}</dd>
            </>
          )}
          {communicationNeeds && (
            <>
              <dt className="text-neutral-600">Communication needs</dt>
              <dd className="whitespace-pre-line text-neutral-900">{communicationNeeds}</dd>
            </>
          )}
        </dl>
        {identifiersVisible && <SecureInformation clientId={banner.client_id} />}
        {/* Below the facts, at the bottom right, in the accent (4.6 : 1 on the panel; light blue in dark):
            never in the panel's actions row, which would push the picture down. */}
        {mayEdit && (
          <p className="flex justify-end">
            <Link to={`/clients/${banner.client_id}/details`}
                  className="link inline-flex min-h-[44px] items-center text-[0.9375rem] font-semibold">
              Edit details
            </Link>
          </p>
        )}
      </div>
    </Panel>
  );
}

/**
 * "Secure information" on the Client card (Arthur, 16 October; it reverses 15 October's "never on the
 * Dashboard"): one masked line per RECORDED number, and one button that shows them all.
 *
 * - Drawn only when the summary says this reader may see identifiers; anyone else's browser is sent nothing
 *   about them. A kind not recorded has no line; with none recorded there is no block at all.
 * - "Show secure information" asks reveal-all, which commits one audit row per number before any value
 *   leaves the API. The values live only in this component's state: never a query cache, never storage.
 * - Hidden after 30 seconds, by "Hide secure information", and whenever the card unmounts (leaving the
 *   client or the Dashboard, or a reload), because the state goes with it.
 * - Add, Change, Remove, the single Show and the history stay in Edit details.
 */
function SecureInformation({ clientId }: { clientId: string }) {
  const ids = useClientIdentifiers(clientId, true);
  const reveal = useRevealAllIdentifiers(clientId);
  const [shown, setShown] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    if (shown === null) return;
    const t = setTimeout(() => setShown(null), 30_000);
    return () => clearTimeout(t);
  }, [shown]);

  if (ids.isError) {
    return <LoadFailure what="Secure information" error={ids.error} onRetry={() => void ids.refetch()}
                        retrying={ids.isFetching} forbiddenIsSession={false} />;
  }
  const recorded = (ids.data?.identifiers ?? []).filter((x) => x.last4 !== null);
  if (recorded.length === 0) return null;
  const refusal = reveal.error instanceof ApiError ? reveal.error : reveal.error ? null : undefined;

  return (
    <section aria-label="Secure information" className="grid gap-1.5 border-t border-line pt-2.5">
      <h2 className="text-sm font-semibold text-neutral-700">Secure information</h2>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[0.9375rem]">
        {recorded.map((x) => (
          <div key={x.kind} className="contents">
            <dt className="text-neutral-600">{x.label}</dt>
            <dd className="font-mono tabular-nums text-neutral-900">{shown?.[x.kind] ?? masked(x.kind, x.last4!)}</dd>
          </div>
        ))}
      </dl>
      {ids.data?.available ? (
        <p className="flex justify-end">
          {shown === null ? (
            <button type="button" disabled={reveal.isPending}
                    className="inline-flex min-h-[44px] items-center rounded-lg border border-neutral-400 px-4 text-[0.9375rem] font-semibold text-neutral-800 hover:bg-tint"
                    onClick={() => reveal.mutate(undefined, {
                      onSuccess: (r) => { setShown(Object.fromEntries(r.values.map((v) => [v.kind, v.value]))); reveal.reset(); },
                    })}>
              {reveal.isPending ? 'Showing…' : 'Show secure information'}
            </button>
          ) : (
            <button type="button" onClick={() => setShown(null)}
                    className="inline-flex min-h-[44px] items-center rounded-lg border border-neutral-400 px-4 text-[0.9375rem] font-semibold text-neutral-800 hover:bg-tint">
              Hide secure information
            </button>
          )}
        </p>
      ) : (
        <p role="status" className="text-sm text-warning-fg">
          These numbers cannot be shown right now: the key that protects them is unavailable.
        </p>
      )}
      {refusal !== undefined && (
        <p role="alert" className="rounded-lg border-l-8 border-critical-edge bg-panel px-3 py-2 text-sm text-critical-edge">
          {refusal ? refusal.message : 'These numbers could not be shown.'}
          {refusal?.hint && <span className="block text-neutral-700">{refusal.hint}</span>}
        </p>
      )}
      {shown !== null && <p className="text-sm text-neutral-600">Shown for 30 seconds. Every Show is recorded.</p>}
    </section>
  );
}

function FlagsPanel({ banner }: { banner: ClientBanner }) {
  const { lesser } = splitByBand(banner);
  // A count from the view arrives as text; compared as a number.
  const critical = Number(banner.critical_flags);
  const resolved = banner.resolved_safety_flags ?? [];
  return (
    <Panel title="Flags" span={4}>
      <div className="px-3 py-2 text-sm">
        <p className="text-neutral-700">
          {[...new Set([banner.status, banner.appointment])].filter(Boolean).join(' · ') || 'Active'}
        </p>
        {critical > 0 && (
          <p className="mt-1 font-semibold text-critical-edge">
            {critical === 1 ? 'One critical flag' : `${critical} critical flags`}, shown in full above.
          </p>
        )}
        {lesser.length === 0 && critical === 0 && <p className="mt-1 text-neutral-600">No flags.</p>}
      </div>
      <LesserFlags flags={lesser} />
      {resolved.length > 0 && <ResolvedSafetyFlags flags={resolved} />}
    </Panel>
  );
}

function VisitFacts({ v }: { v: NextVisit }) {
  const done = v.visits_this_year ?? 0;
  const owed = v.required_this_year ?? 0;
  const behind = owed > 0 && done < owed;
  return (
    <div className="grid gap-1 px-3 py-2 text-sm">
      <Row label="Last visit">{v.last_visit ? formatDate(v.last_visit) : 'Never visited'}</Row>
      <Row label="Next due">
        <span className={v.is_overdue ? 'font-semibold text-critical-edge' : ''}>
          {v.due_on ? formatDate(v.due_on) : 'No anchor date'}
          {v.days_until !== null && (v.is_overdue ? ` · ${-v.days_until} days overdue` : v.days_until >= 0 ? ` · in ${v.days_until} days` : ' · within grace')}
        </span>
      </Row>
      <Row label="This year"><span className={behind ? 'font-semibold text-amber-800' : ''}>{done} of {owed}</span></Row>
      {owed > 0 && (
        <div className="h-2 overflow-hidden rounded bg-line" role="img" aria-label={`${done} of ${owed} visits this year`}>
          <div className="h-full bg-accent" style={{ width: `${Math.min(100, Math.round((done / owed) * 100))}%` }} />
        </div>
      )}
      {v.basis && <p className="text-xs text-neutral-600">{v.basis}</p>}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-line pb-1 last:border-b-0">
      <span className="text-neutral-600">{label}</span>
      <span className="text-right tabular-nums text-neutral-900">{children}</span>
    </div>
  );
}

/**
 * People on the case (round 3; section 13, 15 October): one read-only panel, in three groups.
 *
 * - Allied: the Social Work and Finance case managers and anyone covering. Shown
 *   to anyone who can see the client. Assigning and covering stay in Social
 *   Work's Case team panel.
 * - Court: three lines, always, each its own: the judge, the court evaluator and
 *   the court examiner. The evaluator investigates for the court before the
 *   hearing; the examiner reviews Allied's accounts after appointment. Different
 *   people in different roles, never one under the other. A guardian ad litem
 *   and co-guardians follow, each on a line of its own.
 * - Counsel: for Allied (117's role) and for the client. Petitioning attorney
 *   and adverse counsel name no side, and are listed as what they are.
 * Court and Counsel follow the Legal section: absent for a reader without it.
 * They are recorded from their own page, opened by this panel's Edit button (Arthur, 23 October, A-#97;
 * they were in Edit details): Legal at write, the same rule the routes refuse with.
 */
function PeoplePanel({ clientId, hasLegal, mayEdit }: { clientId: string; hasLegal: boolean; mayEdit: boolean }) {
  const q = useClientPeople(clientId);
  const d = q.data;
  const sw = d?.allied.filter((m) => m.department === 'Social Work' && m.assignment_kind === 'Primary') ?? [];
  const fin = d?.allied.filter((m) => m.department === 'Finance' && m.assignment_kind === 'Primary') ?? [];
  const cover = d?.allied.filter((m) => m.assignment_kind === 'Covering') ?? [];
  const judge = d?.court
    ? d.court.judge
      ? `${d.court.judge}${d.court.court_name ? `, ${d.court.court_name}` : ''}`
      : d.court.judge_from_order
        ? `${d.court.judge_from_order.name} (on the order${d.court.judge_from_order.signed_date ? ` signed ${formatDate(d.court.judge_from_order.signed_date)}` : ''})`
        : null
    : null;
  return (
    <Panel title="People on the case" span={4} rows2>
      {q.error && !d ? (
        <LoadFailure what="People on the case" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />
      ) : !d ? <Waiting /> : (
        <div className="grid gap-1 px-3 py-2 text-sm">
          <Group>Allied</Group>
          <People rows={[
            ['Social Work', sw.map((m) => m.staff_name).join('; ') || null],
            ['Finance', fin.map((m) => m.staff_name).join('; ') || null],
            ['Covering', cover.map((m) => `${m.staff_name}${m.covers_until ? ` until ${formatDate(m.covers_until)}` : ''}`).join('; ') || 'Nobody'],
          ]} />
          {hasLegal && d.court && (
            <>
              <Group>Court Information</Group>
              <People rows={[
                ['Judge', judge],
                ['Court evaluator', names(d.court.evaluator)],
                ['Court examiner', names(d.court.examiner)],
                ...d.court.others.map((c) => [c.role_label, names([c])] as [string, string | null]),
              ]} />
            </>
          )}
          {hasLegal && d.counsel && (
            <>
              <Group>Counsel</Group>
              <People rows={[
                ['For Allied', names(d.counsel.for_allied)],
                ['For the client', names(d.counsel.for_client)],
                ...d.counsel.other.map((c) => [c.role_label, names([c])] as [string, string | null]),
              ]} />
            </>
          )}
        </div>
      )}
      {/* Its own edit (A-#104): a text link at the bottom right, exactly as the Client card's Edit details. */}
      {mayEdit && (
        <p className="flex justify-end px-3">
          <Link to={peopleEditPath(clientId)}
                className="link inline-flex min-h-[44px] items-center text-[0.9375rem] font-semibold">
            Edit people
          </Link>
        </p>
      )}
    </Panel>
  );
}

function names(rows: CaseContact[]): string | null {
  const n = rows.map((r) => [r.name ?? 'Name not recorded', r.credentials].filter(Boolean).join(', ') + (r.do_not_contact ? ' (do not contact)' : ''));
  return n.length ? n.join('; ') : null;
}

function Group({ children }: { children: ReactNode }) {
  return <h3 className="mt-1 text-xs font-semibold tracking-[0.06em] text-brand-text uppercase">{children}</h3>;
}

function People({ rows }: { rows: [string, string | null][] }) {
  // One label width for every group, so the names line up down the panel.
  return (
    <dl className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-3 gap-y-1">
      {rows.map(([label, value], i) => (
        <div key={`${label}-${i}`} className="contents">
          <dt className="text-neutral-600">{label}</dt>
          <dd className={value ? 'text-neutral-900' : 'text-neutral-600 italic'}>{value ?? 'Not recorded'}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Contacts (contacts module, 16 October): the client's own people, grouped Family and next of kin,
 * Clinical, Financial and Other, current first and former collapsed; the people for each of their
 * properties; and the building they live in with its contacts, as the facility page shows them to
 * anyone. The court's people are People on the case's, and are not repeated here.
 */
function ContactsPanel({ clientId }: { clientId: string }) {
  const q = useClientContacts(clientId);
  const d = q.data;
  const [adding, setAdding] = useState(false);
  const groups: [string, string[]][] = [
    ['Family and next of kin', ['Family']],
    ['Clinical', ['Clinical']],
    ['Financial', ['Financial']],
    ['Other', ['Other', 'Facility', 'Property']],
  ];
  return (
    <Panel title="Contacts" span={4}
           actions={d?.may_add ? <button type="button" className={addButton} onClick={() => setAdding(true)}>Add a contact</button> : undefined}>
      {q.error && !d ? (
        <LoadFailure what="Contacts" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />
      ) : !d ? <Waiting /> : (
        <div className="grid gap-2 px-3 py-2 text-sm">
          {groups.map(([title, cats]) => {
            const links = d.own.filter((l) => cats.includes(l.category));
            if (links.length === 0 && title !== 'Family and next of kin') return null;
            return (
              <div key={title}>
                <Group>{title}</Group>
                <ContactLinkList links={links} empty="None recorded." />
              </div>
            );
          })}
          {d.property.length > 0 && (
            <div>
              <Group>Property</Group>
              <ContactLinkList links={d.property} empty="None recorded." />
            </div>
          )}
          <div>
            <Group>Facility</Group>
            {!d.facility ? (
              <p className="text-neutral-600">Not in a facility, so there are no building contacts.</p>
            ) : (
              <>
                <Link to={`/facilities/${d.facility.id}`} className="link inline-flex min-h-[44px] items-center font-medium">{d.facility.facility_name}</Link>
                {d.facility.street && <span className="text-neutral-600"> · {[d.facility.street, d.facility.city].filter(Boolean).join(', ')}</span>}
                {d.facility_contacts.length === 0 ? (
                  <p className="mt-1 text-neutral-600">No contacts are recorded for this facility yet.</p>
                ) : (
                  <ul className="mt-1 divide-y divide-line">
                    {d.facility_contacts.map((c) => (
                      <li key={c.link_id} className="py-1">
                        <span className="font-medium text-neutral-900">{c.name ?? 'Name not recorded'}</span>
                        <span className="block text-neutral-600">{[c.job_title, c.role_label].filter(Boolean).join(' · ')}</span>
                        <span className="block text-neutral-700">{[c.phone, c.email].filter(Boolean).join(' · ')}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          {d.legal_in_people && <p className="text-neutral-600">The court’s people and counsel are under People on the case.</p>}
        </div>
      )}
      {adding && <AddContactSheet target={{ client_id: clientId }} onClose={() => setAdding(false)} />}
    </Panel>
  );
}

const addButton =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50';

/** Past addresses, read-only and most recent first; recording and dating them stays in Social Work. */
function PastAddressesPanel({ clientId }: { clientId: string }) {
  const q = usePastAddresses(clientId);
  const rows = [...(q.data?.past_addresses ?? [])].sort((a, b) =>
    (b.moved_out_on ?? '').localeCompare(a.moved_out_on ?? '') || b.created_at.localeCompare(a.created_at));
  return (
    <Panel title="Past addresses" span={4}>
      {q.error && !q.data ? (
        <LoadFailure what="Past addresses" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />
      ) : !q.data ? <Waiting /> : (
        <div className="px-3 py-2 text-sm">
          {rows.length === 0 ? <p className="text-neutral-600">No past addresses are recorded.</p> : (
            <ul className="divide-y divide-line">
              {rows.map((a) => (
                <li key={a.id} className="flex justify-between gap-3 py-1">
                  <span className="min-w-0 text-neutral-900">{[[a.street_address, a.unit].filter(Boolean).join(' '), a.city, a.state].filter(Boolean).join(', ')}</span>
                  <span className="shrink-0 tabular-nums text-neutral-600">{a.moved_out_on ? `to ${formatDate(a.moved_out_on)}` : 'date not recorded'}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to={`/clients/${clientId}/social-work`} className="link inline-flex min-h-[44px] items-center">Edit in Social Work</Link>
        </div>
      )}
    </Panel>
  );
}

function DueList({ heading, empty, items }: {
  heading: string;
  empty: string;
  items: { key: string; label: string; due: string | null; days: number | null; note: string | null;
           action?: { to: string; text: string; state?: object } }[];
}) {
  return (
    <div className="mt-1">
      <h3 className="text-xs font-semibold tracking-[0.06em] text-brand-text uppercase">{heading}</h3>
      {items.length === 0 ? <p className="text-sm text-neutral-600">{empty}</p> : (
        <ul className="divide-y divide-line">
          {items.map((i) => (
            <li key={i.key} className="flex min-h-[40px] flex-wrap items-center gap-x-3 py-1 text-sm">
              <span className="min-w-0 flex-1 text-neutral-900">{i.label}</span>
              {i.note && <span className="text-xs text-neutral-600">{i.note}</span>}
              <span className={`tabular-nums ${i.days !== null && i.days < 0 ? 'font-semibold text-critical-edge' : 'text-neutral-700'}`}>
                {i.due ? formatDate(i.due) : 'No date'}
                {i.days !== null && (i.days < 0 ? ` · ${-i.days} days overdue` : ` · in ${i.days} days`)}
              </span>
              {i.action && (
                <Link to={i.action.to} state={i.action.state} className="link inline-flex min-h-[44px] items-center font-semibold">{i.action.text}</Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Waiting() {
  return <p className="px-3 py-3 text-sm text-neutral-600">Loading…</p>;
}
